/**
 * Wishlist API Service
 * 
 * Wunschliste für Mangas und Bände
 * 
 * Endpoints:
 * GET    /wishlist              - Wishlist laden
 * POST   /wishlist              - Zur Wishlist hinzufügen
 * DELETE /wishlist/{wishlist_id} - Eintrag löschen
 */

import { get, post, del } from '../client';

/**
 * Wishlist laden
 * @returns {Promise<{success: boolean, data?: WishlistItem[], error?: object}>}
 * 
 * Response Item:
 * {
 *   wishlist_id: number,
 *   manga_id: number,
 *   manga_title: string,
 *   manga_cover_media_key: string,
 *   band_id: number|null,
 *   band_number: number|null,
 *   band_title: string|null,
 *   band_cover_media_key: string|null,
 *   created_at: string (ISO Date)
 * }
 */
export const getWishlist = async () => {
  return get('/wishlist');
};

/**
 * Manga zur Wishlist hinzufügen (ohne spezifischen Band)
 * @param {number} mangaId - Manga ID
 * @returns {Promise<{success: boolean, data?: {message: string}, error?: object}>}
 * 
 * Errors: 409 ALREADY_EXISTS
 */
export const addManga = async (mangaId) => {
  return post('/wishlist', { manga_id: mangaId });
};

/**
 * Spezifischen Band zur Wishlist hinzufügen
 * @param {number} mangaId - Manga ID
 * @param {number} bandId - Band ID
 * @returns {Promise<{success: boolean, data?: {message: string}, error?: object}>}
 * 
 * Errors: 409 ALREADY_EXISTS
 */
export const addBand = async (mangaId, bandId) => {
  return post('/wishlist', { manga_id: mangaId, band_id: bandId });
};

/**
 * Spezifischen Band per Bandnummer zur Wishlist hinzufügen (Option B)
 * @param {number} mangaId - Manga ID
 * @param {number} bandNumber - Bandnummer
 * @returns {Promise<{success: boolean, data?: {message: string}, error?: object}>}
 *
 * Errors: 409 ALREADY_EXISTS
 */
export const addBandByNumber = async (mangaId, bandNumber) => {
  return post('/wishlist', { manga_id: mangaId, band_number: bandNumber });
};

/**
 * Eintrag aus Wishlist löschen
 * @param {number} wishlistId - Wishlist Entry ID
 * @returns {Promise<{success: boolean, data?: {message: string}, error?: object}>}
 * 
 * Errors: 404 NOT_FOUND
 */
export const removeItem = async (wishlistId) => {
  return del(`/wishlist/${wishlistId}`);
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Prüft ob ein Manga/Band bereits auf der Wishlist ist
 * @param {WishlistItem[]} wishlist - Aktuelle Wishlist
 * @param {number} mangaId - Manga ID
 * @param {number|null} bandId - Band ID (optional)
 * @returns {WishlistItem|null} - Gefundener Eintrag oder null
 */
export const findInWishlist = (wishlist, mangaId, bandId = null) => {
  return wishlist.find(item => {
    if (bandId) {
      return item.manga_id === mangaId && item.band_id === bandId;
    }
    return item.manga_id === mangaId && !item.band_id;
  }) || null;
};

/**
 * Filtert Wishlist nach Manga-only Einträgen
 * @param {WishlistItem[]} wishlist - Wishlist
 * @returns {WishlistItem[]} - Nur Manga-Einträge (ohne Band)
 */
export const getMangaOnlyItems = (wishlist) => {
  return wishlist.filter(item => !item.band_id);
};

/**
 * Filtert Wishlist nach Band-Einträgen
 * @param {WishlistItem[]} wishlist - Wishlist
 * @returns {WishlistItem[]} - Nur Band-Einträge
 */
export const getBandItems = (wishlist) => {
  return wishlist.filter(item => item.band_id);
};

// ============================================================================
// TYPES (JSDoc)
// ============================================================================

/**
 * @typedef {object} WishlistItem
 * @property {number} wishlist_id
 * @property {number} manga_id
 * @property {string} manga_title
 * @property {string} manga_cover_media_key
 * @property {number|null} band_id
 * @property {number|null} band_number
 * @property {string|null} band_title
 * @property {string|null} band_cover_media_key
 * @property {string} created_at
 */
